import axios from 'axios';

// Use relative URL so it works with any hostname
const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await api.post('/auth/refresh', null, {
            headers: { Authorization: `Bearer ${refreshToken}` }
          });

          const { access_token } = response.data;
          localStorage.setItem('access_token', access_token);
          api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

          return api(originalRequest);
        }
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// API service methods
export const authService = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
};

export const domainService = {
  list: (params) => api.get('/domains', { params }),
  get: (id) => api.get(`/domains/${id}`),
  import: (sourceId) => api.post(`/domains/import`, { source_id: sourceId }),
};

export const orderService = {
  list: (params) => api.get('/orders', { params }),
  get: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  action: (id, action) => api.post(`/orders/${id}/actions`, { action }),
};

export const sourceService = {
  list: (params) => api.get('/sources', { params }),
  get: (id) => api.get(`/sources/${id}`),
  create: (data) => api.post('/sources', data),
  update: (id, data) => api.put(`/sources/${id}`, data),
  delete: (id) => api.delete(`/sources/${id}`),
  test: (id) => api.post(`/sources/${id}/test`),
  import: (id) => api.post(`/sources/${id}/import`),
  schedule: (id, data) => api.post(`/sources/${id}/schedule`, data),
};

export const userService = {
  list: (params) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

export const managerService = {
  list: (params) => api.get('/managers', { params }),
  get: (id) => api.get(`/managers/${id}`),
  create: (data) => api.post('/managers', data),
  update: (id, data) => api.put(`/managers/${id}`, data),
  delete: (id) => api.delete(`/managers/${id}`),
};

export const eppService = {
  listAccounts: (params) => api.get('/epp/accounts', { params }),
  getAccount: (id) => api.get(`/epp/accounts/${id}`),
  createAccount: (data) => api.post('/epp/accounts', data),
  updateAccount: (id, data) => api.put(`/epp/accounts/${id}`, data),
  deleteAccount: (id) => api.delete(`/epp/accounts/${id}`),
  testAccount: (id) => api.post(`/epp/accounts/${id}/test`),
  getLogs: (accountId, params) => api.get(`/epp/accounts/${accountId}/logs`, { params }),
};

export const paymentService = {
  list: (params) => api.get('/billing/payments', { params }),
  get: (id) => api.get(`/billing/payments/${id}`),
  create: (data) => api.post('/billing/payments', data),
  refund: (id, data) => api.post(`/billing/payments/${id}/refund`, data),
};

export const invoiceService = {
  list: (params) => api.get('/billing/invoices', { params }),
  get: (id) => api.get(`/billing/invoices/${id}`),
  create: (data) => api.post('/billing/invoices', data),
  send: (id) => api.post(`/billing/invoices/${id}/send`),
  download: (id) => api.get(`/billing/invoices/${id}/download`, { responseType: 'blob' }),
};

export const auditService = {
  list: (params) => api.get('/logs', { params }),
  get: (id) => api.get(`/logs/${id}`),
};